import React, { ElementType, FC, forwardRef, RefAttributes, useRef } from 'react';
import { CoreFormControl, CoreFormControlProps } from '../core-form-control';
import { Box, IconButton, InputBaseProps, Select, SelectProps, SxProps, Theme } from '@mui/material';
import { selectPaperStyles } from './core-select.styles';
import { DropdownArrow } from './dropdown-arrow.component';
import { CoreInput, CoreInputProps } from '../core-input';
import { Padding } from '@mui/icons-material';

export interface CoreSelectProps extends CoreFormControlProps, InputBaseProps, CoreInputProps {
	SelectProps?: SelectProps;
	IconComponent?: any;
	disableIconRotate?: boolean;
	iconSize?: number;
	paperSx?: SxProps<Theme>;
	fitMenuWidth?: boolean;
	menuMinWidth?: number;
	onClose?(): void;
	renderLabel?(selected: unknown): any;
}

export const CoreSelect: FC<Omit<CoreSelectProps, 'ref'> & RefAttributes<unknown>> = forwardRef((props, ref) => {
	const {
		menuMinWidth,
		children,
		focused,
		helperText,
		error,
		small,
		controlSx,
		SelectProps,
		disabled,
		fullWidth,
		label,
		labelHint,
		requiredMark,
		id,
		IconComponent,
		disableIconRotate,
		iconSize = 16,
		onClose,
		paperSx,
		fitMenuWidth,
		placeholder,
		renderLabel,
		...inputProps
	} = props;

	const selectContainerRef = useRef<HTMLDivElement | null>(null);
	const offsetWidth = selectContainerRef.current?.offsetWidth;

	const inputHeight = small ? 30 : 48;

	const getLabelFromValue = (value: any, children: React.ReactNode) =>
		React.Children.toArray(children)
			?.filter((child): child is React.ReactElement => React.isValidElement(child))
			.find((child) => child.props.value === value)?.props.children || value;

	return (
		<CoreFormControl
			label={label}
			labelHint={labelHint}
			id={id}
			requiredMark={requiredMark}
			fullWidth={fullWidth}
			focused={focused}
			controlSx={controlSx}
			helperText={helperText}
			error={error}
			disabled={disabled}
		>
			<Select
				{...SelectProps}
				ref={selectContainerRef}
				sx={{
					height: `${inputHeight}px`,
					display: 'flex',
					alignItems: 'center',
					'& .MuiSelect-icon': {
						right: small ? 12 : 16,
						top: (inputHeight - iconSize) / 2,
						transform: disableIconRotate ? 'none' : undefined,
					},
					'& .MuiInputBase-input': {
						height: `${inputHeight - 16}px`,
						lineHeight: `${inputHeight - 16}px`,
						padding: '0 16px',
					},
				}}
				MenuProps={{
					...SelectProps?.MenuProps,
					TransitionProps: {
						onExited: onClose,
					},
					PaperProps: {
						sx: [
							{
								width: fitMenuWidth
									? (menuMinWidth ?? 0) > (offsetWidth ?? 0)
										? menuMinWidth
										: offsetWidth
									: undefined,
							},
							...(Array.isArray(selectPaperStyles) ? selectPaperStyles : [selectPaperStyles]),
							...(Array.isArray(paperSx) ? paperSx : [paperSx]),
						],
					},
				}}
				// SelectDisplayProps={{
				// 	style: { marginRight: small ? 40 : 44 },
				// }}
				IconComponent={IconComponent ?? DropdownArrow}
				displayEmpty
				renderValue={(selected) => {
					if (!selected) {
						return (
							<Box
								component="span"
								sx={{ color: (theme) => (disabled ? theme.colors.blueTransparent : theme.colors.gray) }}
							>
								{placeholder}
							</Box>
						);
					}

					if (SelectProps?.renderValue) {
						return (
							<Box component="span" sx={{ color: (theme) => (disabled ? theme.colors.blueTransparent : '') }}>
								{SelectProps.renderValue(selected)}
							</Box>
						);
					} else {
						return (
							<Box component="span" sx={{ color: (theme) => (disabled ? theme.colors.blueTransparent : '') }}>
								{getLabelFromValue(selected, children)}
							</Box>
						);
					}
				}}
				input={<CoreInput {...inputProps} ref={ref} small={small} id={id} />}
			>
				{children}
			</Select>
		</CoreFormControl>
	);
});
